import { GiftCardFormData } from '@pb/components'
import {
  recoverRequestFromFailure,
  isHmBrand,
  isNorthstarBrand,
  consoleEvents,
  trackViewEvent,
  trackEvent,
} from '@pb/utils'

import { PAYMENT_PROVIDERS, TRACKING_EVENTS } from 'App/constants'
import appActions from 'App/duck/actions'
import appSelectors from 'App/duck/selectors'
import OPTIMIZELY from 'constants/optimizely'
import { completeAdyenCheckout } from 'features/adyen/duck/actions'
import { operationProcessAdyenCheckoutResponse } from 'features/adyen/duck/operations'
import getBrowserInfo from 'features/adyen/helpers/getBrowserInfo'
import {
  getABTestData,
  getShoppingBagTrackingInfo,
  getCartItemTrackingInfo,
  getGiftCardTrackingInfo,
  getGiftCardErrorMessage,
} from 'features/cart/cart-overview/helpers/getCartTracking'
import { CART_ERROR_GROUPS } from 'features/cart/constants'
import { operationFetchCartPRA } from 'features/cart/pra/duck/operations'
import { CHECKOUT_REDIRECT_ACTIONS, CheckoutSuccessAction } from 'features/checkout/constants'
import { deleteGiftCard, postAddGiftCard } from 'features/checkout/requests'
import { moveToFavouriteFromCart } from 'features/favourites/requests'
import { setOrderTotals } from 'features/orderTotals/duck/actions'
import { setAppliedVouchers } from 'features/vouchers/duck/actions'
import getErrorData from 'helpers/getErrorData'
import { trackOptimizelyEvent } from 'helpers/optimizely'
import pLimit from 'helpers/pLimit'
import { setABTestingData, CLICKTALE_SIGNALS } from 'helpers/tracking'
import updateMiniCart from 'helpers/updateMiniCart'
import { ThunkResult } from 'redux/types'

import { GIFT_CARD_ERROR_MESSAGES, GIFT_CARD_ERROR_AS_INFO_MESSAGE } from '../helpers/constants'
import {
  fetchCart,
  fetchOneClickCheckoutInfo,
  updateQuantity,
  deleteCartItem,
  performOneClickCheckout,
  performConfirmOneClickCheckout,
  fetchMinicart,
} from '../requests'
import {
  addGiftCardFail,
  addGiftCardStart,
  addGiftCardSuccess,
  deleteCartItemFail,
  deleteCartItemStart,
  deleteCartItemSuccess,
  deleteHiddenCartItem,
  fetchCartFail,
  fetchCartStart,
  fetchCartSuccess,
  fetchHiddenCartItems,
  moveHiddenCartItem,
  moveToFavouriteFail,
  moveToFavouriteStart,
  moveToFavouriteSuccess,
  oneClickCheckoutFail,
  oneClickCheckoutStart,
  oneClickCheckoutSuccess,
  removeGiftCardFail,
  removeGiftCardStart,
  removeGiftCardSuccess,
  setPickUpInStoreData,
  setMaxGiftCardError,
  setOneClickCheckoutPaymentData,
  updateCartItemQuantityFail,
  updateCartItemQuantityStart,
  updateCartItemQuantitySuccess,
  showMiddlePage,
  hideMiddlePage,
} from './actions'
import { normalizeCart, normalizeHiddenCart } from './normalize'
import selectors from './selectors'

const limit = pLimit(1)

const concatCartItemsArrays = (cart): any => {
  return cart.items.concat(cart.hazmatItems)
}

const mutateInStockWithCartState = (normalizedCart, cartState): void => {
  normalizedCart.items.map((item) => {
    const found = cartState.items.find((stateItem) => item.variantCode === stateItem.variantCode)
    if (found) {
      item.inStock = found.inStock
    }
    return item
  })
}
const clone = (val): ReturnType<JSON['parse']> => JSON.parse(JSON.stringify(val))

export const operationFetchCart =
  (
    isInitialCall?: boolean,
    shouldUpdateMiniCart = true,
    shouldTrackViewEvent = true,
    availabilityCheck = true,
  ) =>
  async (dispatch, getState) => {
    dispatch(fetchCartStart())

    const state = getState()
    const shouldFetchOneClickCheckoutInfo =
      process.env.IS_ONE_CLICK_CHECKOUT_ENABLED === 'enabled' &&
      appSelectors.getIsOneClickCheckoutEnabled(state) &&
      appSelectors.getIsSignedIn(state) &&
      isInitialCall

    try {
      const [cartResponse, oneClickCheckoutInfoResponse] = await Promise.all([
        availabilityCheck ? fetchCart() : fetchMinicart(),
        shouldFetchOneClickCheckoutInfo
          ? recoverRequestFromFailure(fetchOneClickCheckoutInfo, {})()
          : Promise.resolve(),
      ])
      const { cart } = getState()
      const normalizedCart = normalizeCart(cartResponse)
      const normalizedHiddenCart = normalizeHiddenCart(cartResponse)

      if (!availabilityCheck) {
        // ignore any availability returned from the /minicart api endpoint since the response always returns items as out of stock
        mutateInStockWithCartState(normalizedCart, cart)
        if (normalizedCart.oosItems.length > 0) {
          normalizedCart.oosItems = clone(cart.oosItems)
        }
      }

      dispatch(
        fetchCartSuccess({
          ...normalizedCart,
          oneClickCheckoutInfo: oneClickCheckoutInfoResponse,
          availabilityCheck,
        }),
      )
      dispatch(fetchHiddenCartItems(normalizedHiddenCart))

      dispatch(setAppliedVouchers(cartResponse.vouchersApplied))
      dispatch(setOrderTotals(cartResponse.orderTotals))
      dispatch(setPickUpInStoreData(cartResponse.storeData))

      // This should only be done when the cart is loaded the first time,
      // i.e. not when fetch cart is triggered by a voucher update.
      if (process.env.IS_CART_PRA_ENABLED === 'enabled' && isInitialCall) {
        dispatch(operationFetchCartPRA(normalizedCart?.praFilterCategories))
      }

      if (shouldTrackViewEvent) {
        trackViewEvent({
          ...TRACKING_EVENTS.viewShoppingBag.data,
          ...getShoppingBagTrackingInfo(getState()),
          ...((isHmBrand || isNorthstarBrand) && { clickTale: CLICKTALE_SIGNALS.start }),
        })

        trackOptimizelyEvent(OPTIMIZELY.PAGEVIEW_SHOPPINGBAG)
      }
      setABTestingData(getABTestData(getState()))

      if (!shouldUpdateMiniCart) return

      if (normalizedCart && !(isHmBrand || isNorthstarBrand)) {
        updateMiniCart(concatCartItemsArrays(normalizedCart))
      } else {
        updateMiniCart()
      }
    } catch (e) {
      dispatch(fetchCartFail(e))
    }
  }

export const operationUpdateCartItemQuantity =
  (variantCode, newQuantity) => async (dispatch, getState) => {
    const state = getState()
    const item = selectors.getCartItem(variantCode, state)
    const currentQuantity = item?.quantity || 0
    const quantityDiff = newQuantity - currentQuantity

    const commonTrackEventData = {
      ...getCartItemTrackingInfo(variantCode, state),
      product_quantity: [Math.abs(quantityDiff).toString()],
    }

    if (quantityDiff < 0) {
      trackEvent({
        ...TRACKING_EVENTS.decreaseProductQuantityInBag.data,
        ...commonTrackEventData,
      })
      trackOptimizelyEvent(OPTIMIZELY.SHOPPINGBAG_DECREASE_PRODUCT_QUANTITY)
    } else {
      trackEvent({
        ...TRACKING_EVENTS.increaseProductQuantityInBag.data,
        ...commonTrackEventData,
      })
      trackOptimizelyEvent(OPTIMIZELY.SHOPPINGBAG_INCREASE_PRODUCT_QUANTITY)
    }

    dispatch(updateCartItemQuantityStart(variantCode, newQuantity))

    try {
      const response = await updateQuantity(variantCode, newQuantity)
      const cart = normalizeCart(response)

      dispatch(
        updateCartItemQuantitySuccess({
          ...cart,
          ...{
            availabilityCheck: true,
          },
        }),
      )
      dispatch(setOrderTotals(cart.orderTotals))
      dispatch(setPickUpInStoreData(cart.storeData))

      if (cart && !(isHmBrand || isNorthstarBrand)) {
        updateMiniCart(concatCartItemsArrays(cart))
      } else {
        updateMiniCart()
      }
    } catch (e) {
      dispatch(updateCartItemQuantityFail(e))
      trackEvent({
        ...(quantityDiff > 0
          ? TRACKING_EVENTS.increaseProductQuantityInBagError.data
          : TRACKING_EVENTS.decreaseProductQuantityInBagError.data),
      })
    }
  }

export const operationDeleteCartItem = (variantCode) => async (dispatch, getState) => {
  const state = getState()
  const item = selectors.getCartItem(variantCode, state)

  trackEvent({
    ...TRACKING_EVENTS.removeFromBag.data,
    ...getCartItemTrackingInfo(variantCode, state),
    product_quantity: [item?.quantity.toString() || 0],
  })
  trackOptimizelyEvent(OPTIMIZELY.CLICK_SB_REMOVE_ITEM)

  dispatch(deleteCartItemStart(variantCode))

  try {
    const response = await limit(() => deleteCartItem(variantCode))
    const cart = normalizeCart(response)

    dispatch(deleteCartItemSuccess(cart))
    dispatch(deleteHiddenCartItem(variantCode))
    dispatch(setOrderTotals(cart.orderTotals))
    dispatch(setPickUpInStoreData(cart.storeData))

    if (cart && !(isHmBrand || isNorthstarBrand)) {
      updateMiniCart(concatCartItemsArrays(cart))
    } else {
      updateMiniCart()
    }
  } catch (e) {
    dispatch(deleteCartItemFail(e))
    trackEvent({
      ...TRACKING_EVENTS.removeFromBagError.data,
    })
  }
}

export const operationMoveToFavourite = (variantCode) => async (dispatch, getState) => {
  const state = getState()
  // Save current state to variable to put back later if request fails.
  const previousCartState = state.cart
  dispatch(moveToFavouriteStart(variantCode))

  try {
    const response = await limit(() => moveToFavouriteFromCart(variantCode))
    const cart = normalizeCart(response)

    dispatch(moveToFavouriteSuccess(cart))
    dispatch(moveHiddenCartItem(variantCode))
    dispatch(setOrderTotals(cart.orderTotals))
    dispatch(setPickUpInStoreData(cart.storeData))

    trackEvent({
      ...TRACKING_EVENTS.addToFavourites.data,
      ...getCartItemTrackingInfo(variantCode, state),
    })

    trackEvent({
      ...TRACKING_EVENTS.removeFromBag.data,
      ...getCartItemTrackingInfo(variantCode, state),
    })

    trackOptimizelyEvent(OPTIMIZELY.CLICK_SB_ADD_TO_FAVOURITES)

    if (cart && !(isHmBrand || isNorthstarBrand)) {
      updateMiniCart(concatCartItemsArrays(cart))
    } else {
      updateMiniCart()
    }
  } catch (error) {
    dispatch(moveToFavouriteFail(previousCartState))
    dispatch(
      // @ts-expect-error ts-migrate(2554) FIXME: Expected 0 arguments, but got 1.
      appActions.showErrorModal({
        error: error.response,
        errorGroup: CART_ERROR_GROUPS.FAVOURITES,
      }),
    )
    trackEvent({
      ...TRACKING_EVENTS.moveToFavouritesError.data,
    })
  }
}

export const operationPerformOneClickCheckout =
  (shouldSkipModal, selectedPaymentMethod) => async (dispatch) => {
    dispatch(oneClickCheckoutStart())

    const browserInfo = getBrowserInfo()

    const requestPageAttributes =
      selectedPaymentMethod === PAYMENT_PROVIDERS.ADYEN
        ? Object.keys(browserInfo).reduce(
            (acc, key) => ({
              ...acc,
              [`browserInfo.${key}`]: browserInfo[key],
            }),
            {},
          )
        : null

    try {
      const oneClickCheckoutResponse = await performOneClickCheckout(
        shouldSkipModal,
        requestPageAttributes,
      )
      const { pageAttributes, paymentProvider, redirectUrl, signedHtml, successAction } =
        oneClickCheckoutResponse

      let shouldCompleteOneClickCheckout = true

      if (
        successAction === CheckoutSuccessAction.ExternalRedirect &&
        paymentProvider === PAYMENT_PROVIDERS.PAYPAL
      ) {
        dispatch(
          setOneClickCheckoutPaymentData({
            paymentProvider,
            ...pageAttributes,
          }),
        )
      } else if (
        paymentProvider === PAYMENT_PROVIDERS.ADYEN &&
        successAction !== CheckoutSuccessAction.SelfRedirect
      ) {
        shouldCompleteOneClickCheckout = false

        dispatch(operationProcessAdyenCheckoutResponse(oneClickCheckoutResponse))
        dispatch(
          setOneClickCheckoutPaymentData({
            paymentProvider,
          }),
        )
      } else if (CHECKOUT_REDIRECT_ACTIONS.includes(successAction)) {
        window.location.href = redirectUrl
      } else if (successAction === CheckoutSuccessAction.FormSubmit) {
        dispatch(
          setOneClickCheckoutPaymentData({
            paymentProvider,
            formContent: signedHtml,
            ...pageAttributes,
          }),
        )
      }

      if (shouldCompleteOneClickCheckout) {
        dispatch(oneClickCheckoutSuccess())
      }
    } catch (error) {
      trackEvent({
        ...TRACKING_EVENTS.oneClickCheckoutError.data,
        event_label: String(error),
      })
      dispatch(oneClickCheckoutFail())
      dispatch(
        // @ts-expect-error ts-migrate(2554) FIXME: Expected 0 arguments, but got 1.
        appActions.showErrorModal({
          error: error.response,
          errorGroup: CART_ERROR_GROUPS.ONE_CLICK_CHECKOUT,
        }),
      )
      consoleEvents.error('Failed to perform express checkout', error)
    }
  }

export const operationConfirmOneClickCheckout = (data) => async (dispatch) => {
  try {
    dispatch(completeAdyenCheckout())
    dispatch(oneClickCheckoutStart())

    const confirmResponse = await performConfirmOneClickCheckout(data)

    if (confirmResponse.successAction === CheckoutSuccessAction.AdyenChallenge) {
      dispatch(operationProcessAdyenCheckoutResponse(confirmResponse))
      dispatch(oneClickCheckoutSuccess())
    } else {
      window.location.href = confirmResponse.redirectUrl
    }
  } catch (error) {
    trackEvent({
      ...TRACKING_EVENTS.oneClickCheckoutError.data,
      event_label: String(error),
    })
    dispatch(oneClickCheckoutFail())
    dispatch(
      // @ts-expect-error ts-migrate(2554) FIXME: Expected 0 arguments, but got 1.
      appActions.showErrorModal({
        error: error.response,
        errorGroup: CART_ERROR_GROUPS.ONE_CLICK_CHECKOUT,
      }),
    )
    consoleEvents.error('Failed to perform express checkout', error)
  }
}

export const operationAddGiftCard =
  (formData: GiftCardFormData): ThunkResult<Promise<void>> =>
  async (dispatch, getState) => {
    dispatch(addGiftCardStart())
    try {
      const cart = await postAddGiftCard(formData, true)
      dispatch(addGiftCardSuccess(cart.giftCardsApplied))
      dispatch(setOrderTotals(cart.orderTotals))

      if (cart.giftCardsApplied.length === 5) {
        const errorData = {
          message: GIFT_CARD_ERROR_MESSAGES.MAXIMUM_GIFT_CARDS_REACHED,
          isWarningMessage: true,
        }
        dispatch(setMaxGiftCardError(errorData))
      }
      const state = getState()
      trackEvent({
        ...TRACKING_EVENTS.addGiftCard.data,
        ...getGiftCardTrackingInfo(formData.cardNumber, state),
      })
    } catch (error) {
      consoleEvents.error('Failed to add gift card', error)
      const { code: responseErrorCode } = getErrorData(error)
      const message = GIFT_CARD_ERROR_MESSAGES[responseErrorCode]
      const isWarningMessage = GIFT_CARD_ERROR_AS_INFO_MESSAGE[responseErrorCode]
      const errorData = {
        message,
        isWarningMessage,
      }
      dispatch(addGiftCardFail(errorData))

      const state = getState()
      trackEvent({
        ...TRACKING_EVENTS.giftCardError.data,
        giftcard_error_message: getGiftCardErrorMessage(state),
        error_function: 'Add gift card',
      })
    }
  }

export const operationRemoveGiftCard =
  (cardNumber: string): ThunkResult<Promise<void>> =>
  async (dispatch, getState) => {
    dispatch(removeGiftCardStart())
    try {
      const state = getState()
      const cart = await deleteGiftCard(cardNumber, true)
      dispatch(removeGiftCardSuccess(cart.giftCardsApplied))
      dispatch(setOrderTotals(cart.orderTotals))

      trackEvent({
        ...TRACKING_EVENTS.removeGiftCard.data,
        ...getGiftCardTrackingInfo(cardNumber, state),
      })
    } catch (error) {
      consoleEvents.error('Failed to remove gift card', error)
      dispatch(removeGiftCardFail())

      const state = getState()
      const errorMessage = getGiftCardErrorMessage(state)
      trackEvent({
        ...TRACKING_EVENTS.giftCardError.data,
        giftcard_error_message: errorMessage,
        error_function: 'Remove gift card',
      })
    }
  }
export const operationShowMiddlePage = (isMiddlePageShow) => async (dispatch) => {
  if (isMiddlePageShow) {
    dispatch(showMiddlePage())
  } else {
    dispatch(hideMiddlePage())
  }
}
